import { useEffect, useState } from "react";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import {
  getBillsFilteringOptions,
  getClearBillingModel,
  getCurrencyFromSetup,
} from "../../common/EntitiesManagement";
import {
  GetDateInDDMMYYYYFormat,
  generateNoDataValues,
  getFiltersInfo,
  getValueInCurrency,
} from "../../common/utils";
import ErrorMessage from "../../components/common/ErrorMessage";
import useGlobalContext from "../../hooks/useGlobalContext";
import ModalBackground from "../../components/common/ModalBackground";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import AddOrEditBill from "./components/AddOrEditBill";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import BillEdition from "./components/BillEdition";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function Bill() {
  const {
    config,
    BILL_ENDPOINT,
    billsData,
    billModel,
    setBillModel,
    pageIndexBill,
    setPageIndexBill,
    toggleVisibleModalConfirm,
    setBasicFilterOptions,
    basicFilterData,
    deleteEntity,
    basicDefaultFiltering,
  } = useGlobalContext();
  const [showProcessResultModal, setShowProcessResultModal] = useState(false);
  const [processResultMessage, setProcessResultMessage] = useState("");
  const [processResultTitle, setProcessResultTitle] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditionMode, setShowEditionMode] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBillsFilteringOptions());
  }, []);

  const validateOperationResult = (result, isDelete) => {
    setBillModel(null);

    if (!result.ok) {
      setProcessResultTitle(localize.delete);
      setProcessResultMessage(JSON.stringify(result.data));
      setShowProcessResultModal(true);
      return;
    }

    mutate([BILL_ENDPOINT, config]);

    if (isDelete) {
      if (pageIndexBill !== 0 && billsData.Data.length === 1) {
        setPageIndexBill(pageIndexBill - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${BILL_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const disableBillChanges = () =>
    !billModel?.Code ||
    billModel?.Code.length === 0 ||
    !billModel?.GenerationDate ||
    billModel?.Value <= 0;

  const getColumnTitles = () => {
    return [
      localize.bill_code,
      localize.bill_date,
      localize.client,
      localize.total_value,
      localize.observations,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  const showPrinter = () => {
    setShowPrinterModal(true);
  };

  return (
    <div className="lateral_padding">
      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          title={localize.print}
          report_title={localize.related_bills}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={BILL_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="related_bills"
        />
      )}

      {showProcessResultModal && (
        <ErrorMessage
          message={processResultMessage}
          title={processResultTitle}
          showModal={showProcessResultModal}
          setShowModal={setShowProcessResultModal}
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_bill}
          okButtonText={localize.continue}
          onClickToSubmit={() => {
            setShowEditModal(false);
            setShowEditionMode(true);
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setBillModel(null);
          }}
          Component={
            <AddOrEditBill
              model={{
                ...billModel,
                ClientId: billModel.Client?.Id,
              }}
              setModel={setBillModel}
            />
          }
          validators={disableBillChanges()}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={billModel.Code}
          entityType={localize.bill}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setBillModel(null);
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(billModel.Id);
            setShowDeleteModal(false);
            setBillModel(null);
          }}
        />
      )}

      {!showEditionMode && (
        <Card
          variant="outlined"
          style={{
            margin: "16px",
            boxShadow:
              "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
          }}
        >
          <CardContent>
            {!showEditionMode && (
              <PageTitleSubtitle
                title={localize.related_bills}
                subtitle={localize.related_bills_description}
              />
            )}

            {!showEditionMode && (
              <SearcherAndAdd
                printButtonMethod={showPrinter}
                defaultFiltering={basicDefaultFiltering}
                entityName="related_bills"
                handleAddBtn={() => {
                  setBillModel(getClearBillingModel());
                  setShowAddModal(true);
                }}
                modal={
                  <ModalBackground
                    isDrawerMode
                    hasRequiredValues
                    showModal={showAddModal}
                    setShowModal={setShowAddModal}
                    title={localize.new_bill}
                    okButtonText={localize.continue}
                    onClickToSubmit={() => {
                      setShowAddModal(false);
                      setShowEditionMode(true);
                    }}
                    onClickToCancel={() => {
                      setShowAddModal(false);
                      setBillModel(null);
                    }}
                    Component={
                      <AddOrEditBill
                        model={billModel}
                        setModel={setBillModel}
                      />
                    }
                    validators={disableBillChanges()}
                  />
                }
              />
            )}
          </CardContent>
        </Card>
      )}

      {showEditionMode ? (
        <BillEdition
          billModel={billModel}
          setBillModel={setBillModel}
          setShow={setShowEditionMode}
        />
      ) : (
        <TableComponent
          pagination={
            <PagePagination
              pageIndex={pageIndexBill}
              setPageIndex={setPageIndexBill}
              dataArray={billsData}
            />
          }
          firstColumnTitle={localize.actions}
          columnTitles={getColumnTitles()}
          results={
            billsData.Data?.length !== 0
              ? billsData.Data.map((item) => ({
                  key: item.Id,
                  data: [
                    <div>
                      <ActionButtons
                        onClickForEditButton={() => {
                          setBillModel(item);
                          setShowEditModal(true);
                        }}
                        onclickForDeleteButton={() => {
                          setBillModel(item);
                          setShowDeleteModal(true);
                          toggleVisibleModalConfirm();
                        }}
                      />
                    </div>,
                    <ValueOrNoDataLabel data={item.Code} />,
                    <ValueOrNoDataLabel
                      data={GetDateInDDMMYYYYFormat(
                        new Date(item.GenerationDate)
                      )}
                    />,
                    <ValueOrNoDataLabel
                      data={
                        item.Client
                          ? `${item.Client.Identification} - ${item.Client.Name}`
                          : null
                      }
                    />,
                    <ValueOrNoDataLabel
                      data={getValueInCurrency(
                        getCurrencyFromSetup(),
                        item.Value ?? 0
                      )}
                    />,
                    <ValueOrNoDataLabel data={item.Observation} />,
                    new Date(item.ModificationDate).toLocaleString(undefined, {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }),
                    item.CreatedBy,
                  ],
                }))
              : [generateNoDataValues(7)]
          }
        />
      )}
    </div>
  );
}
